import { useEffect, useState } from 'react';
import { CheckCircle2, AlertCircle, Plus, Trash2, Building2, Clock, FileText, Palette, Gift, Users, ShieldCheck, HardDrive, CalendarDays, LayoutGrid, Eye, CircleDollarSign, UserRoundCog } from 'lucide-react';
import { supabase, CLINIC_ID } from './supabase.js';
import { loadClinicSettings, saveClinicSettings, loadPrizeCatalog, savePrizeCatalog, saveSatusehatOrganizationMapping } from './data.js';
import { friendlyError, rupiah } from './lib/format.js';
import {
  COLOR_PRESETS, TEMPLATE_PRESETS, WALLPAPER_OPTIONS, FONT_OPTIONS,
  applyAppearance,
} from './lib/appearance.js';
import { PenggunaPage } from './PenggunaPage.jsx';
import { RolesPage } from './RolesPage.jsx';
import { MasterNakesPage } from './MasterNakesPage.jsx';
import { JadwalNakesPage } from './JadwalNakesPage.jsx';
import { ModulesPage } from './ModulesPage.jsx';
import { PatientPortalAccessPage } from './PatientPortalAccessPage.jsx';
import { MasterBiayaPage } from './MasterBiayaPage.jsx';

const NAV_GROUPS = [
  {
    label: 'Klinik',
    items: [
      { key: 'branding',    label: 'Profil & Kontak',      icon: Building2 },
      { key: 'operasional', label: 'Operasional',           icon: Clock },
      { key: 'invoice',     label: 'Invoice & Dokumen',     icon: FileText },
    ],
  },
  {
    label: 'Tampilan',
    items: [
      { key: 'pengalaman',  label: 'Branding & Tampilan',   icon: Palette },
    ],
  },
  {
    label: 'Program',
    items: [
      { key: 'hadiah',      label: 'Hadiah Survey',         icon: Gift },
    ],
  },
  {
    label: 'Tim',
    items: [
      { key: 'pengguna',    label: 'Pengguna',              icon: Users },
      { key: 'roles',       label: 'Role & Akses',          icon: ShieldCheck },
      { key: 'nakes',        label: 'Tenaga Kesehatan',      icon: UserRoundCog },
      { key: 'jadwal_nakes', label: 'Jadwal Nakes',         icon: CalendarDays },
      { key: 'thp_nakes',    label: 'THP Klinik',           icon: CircleDollarSign },
    ],
  },
  {
    label: 'Sistem',
    items: [
      { key: 'master_biaya', label: 'Master Biaya', icon: CircleDollarSign },
      { key: 'patient_portal_access', label: 'Portal Pasien', icon: Eye },
      { key: 'modules',     label: 'Modul Klinik',          icon: LayoutGrid },
      { key: 'backup',      label: 'Backup',                icon: HardDrive },
    ],
  },
];

const SETTINGS_TABS = new Set(['branding','operasional','invoice','pengalaman','hadiah']);

const DENSITY_OPTIONS  = [{ value:'compact', label:'Compact' }, { value:'normal', label:'Normal' }, { value:'comfortable', label:'Comfortable' }];
const ICON_OPTIONS     = [{ value:'outline', label:'Outline' }, { value:'filled', label:'Filled / Tegas' }, { value:'minimal', label:'Minimal' }, { value:'rounded', label:'Rounded' }];
const ANIM_OPTIONS     = [{ value:'normal', label:'Normal' }, { value:'minimal', label:'Minimal' }, { value:'off', label:'Matikan Animasi' }];
const MODE_OPTIONS     = [{ value:'light', label:'Selalu Terang' }, { value:'dark', label:'Selalu Gelap' }, { value:'system', label:'Ikuti Sistem' }];
const LOGIN_STYLE_OPT  = [{ value:'split', label:'Split Branding' }, { value:'centered', label:'Centered' }, { value:'minimal', label:'Minimal' }];

const OPERATING_DAYS = [
  { key: 'SUNDAY', label: 'Minggu' },
  { key: 'MONDAY', label: 'Senin' },
  { key: 'TUESDAY', label: 'Selasa' },
  { key: 'WEDNESDAY', label: 'Rabu' },
  { key: 'THURSDAY', label: 'Kamis' },
  { key: 'FRIDAY', label: 'Jumat' },
  { key: 'SATURDAY', label: 'Sabtu' },
];

function defaultOperatingHours() {
  return Object.fromEntries(OPERATING_DAYS.map(({ key }) => [key, { open: '09:00', close: '20:00' }]));
}

function normalizeOperatingHours(value) {
  const defaults = defaultOperatingHours();
  return Object.fromEntries(OPERATING_DAYS.map(({ key }) => [key, {
    open: value?.[key]?.open || defaults[key].open,
    close: value?.[key]?.close || defaults[key].close,
  }]));
}
const PAYROLL_TYPES = [
  { value: 'Standar', label: 'Standar' },
  { value: 'Nakes Plus', label: 'Nakes Plus' },
  { value: 'Kasir', label: 'Kasir / Non-Klinis' },
  { value: 'MONTHLY', label: 'Bulanan' },
];

function Field({ label, children, span2, span3 }) {
  let cls = 'sp2-field';
  if (span2) cls += ' sp2-field-span2';
  if (span3) cls += ' sp2-field-span3';
  return (
    <div className={cls}>
      <label className="sp2-label">{label}</label>
      {children}
    </div>
  );
}

function PresetCard({ p, active, onClick }) {
  return (
    <div
      className={`spe-preset-card${active ? ' spe-preset-active' : ''}`}
      onClick={() => onClick(p.key)}
    >
      <div className="spe-preset-dots">
        {p.dots.map((c, i) => (
          <span key={i} style={{ background: c }} className="spe-preset-dot" />
        ))}
      </div>
      <div className="spe-preset-label">{p.label}</div>
      <div className="spe-preset-sub">Preview langsung</div>
    </div>
  );
}

function TemplateCard({ t, active, onClick }) {
  const preset = COLOR_PRESETS.find(p => p.key === t.preset);
  const dots = preset?.dots || ['#888','#aaa'];
  return (
    <div
      className={`spe-tpl-card${active ? ' spe-tpl-active' : ''}`}
      onClick={() => onClick(t)}
    >
      <div className="spe-tpl-dots">
        {dots.slice(0,2).map((c, i) => <span key={i} style={{ background: c }} className="spe-preset-dot" />)}
      </div>
      <div>
        <div className="spe-tpl-name">{t.label}</div>
        <div className="spe-tpl-sub">{t.sub}</div>
      </div>
    </div>
  );
}

function LivePreview({ app, clinicName }) {
  const preset = COLOR_PRESETS.find(p => p.key === app.preset) || COLOR_PRESETS[0];
  const primary   = preset.vars['--green-900'] || '#173f34';
  const accent    = preset.vars['--green-700'] || '#2f7d62';
  const paper     = preset.vars['--paper']     || '#fffdf8';
  const cream     = preset.vars['--cream']     || '#f7f1e5';
  const border    = preset.vars['--border']    || '#ded5c4';
  const ink       = preset.vars['--ink']       || '#26352f';
  return (
    <div className="spe-preview">
      {/* Kartu Klinik */}
      <div className="spe-preview-block" style={{ background: cream }}>
        <div className="spe-preview-label">Kartu Klinik</div>
        <div style={{ background: paper, border:`1px solid ${border}`, borderRadius:12, padding:'10px 14px' }}>
          <div style={{ fontSize:10, fontWeight:700, color:accent, textTransform:'uppercase', letterSpacing:'0.1em' }}>Klinik · XV</div>
          <div style={{ fontWeight:700, color:ink, fontSize:13, marginTop:2 }}>{clinicName || 'Nama Klinik'}</div>
          <div style={{ fontSize:10, color:'#888', marginTop:2 }}>nama, tren, dan konten</div>
        </div>
      </div>
      {/* Tombol Utama */}
      <div className="spe-preview-block" style={{ background: cream }}>
        <div className="spe-preview-label">Tombol Utama</div>
        <button style={{ background:accent, color:'white', border:0, borderRadius:10, padding:'8px 20px', fontWeight:700, fontSize:13, cursor:'default', width:'100%' }}>
          Tombol Utama
        </button>
      </div>
      {/* Sidebar */}
      <div className="spe-preview-block" style={{ background: primary, borderRadius:12 }}>
        <div className="spe-preview-label" style={{ color: 'rgba(255,255,255,.6)' }}>Sidebar</div>
        <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
          {['Dashboard','Follow-Up','Pendaftaran'].map((item, i) => (
            <div key={item} style={{
              padding:'5px 10px', borderRadius:8, fontSize:12, fontWeight:600,
              background: i === 0 ? '#f0c979' : 'transparent',
              color: i === 0 ? '#102f27' : 'rgba(255,255,255,.75)',
            }}>{item}</div>
          ))}
        </div>
      </div>
    </div>
  );
}

function InfoField({ label, value, wide = false }) {
  return (
    <div className={wide ? 'settings-info-field settings-info-wide' : 'settings-info-field'}>
      <span>{label}</span>
      <strong>{value || 'Belum diisi'}</strong>
    </div>
  );
}

function SectionActions({ busy, onCancel }) {
  return (
    <div className="settings-section-actions">
      <button type="submit" className="button button-primary" disabled={busy}>{busy ? 'Menyimpan…' : 'Simpan'}</button>
      <button type="button" className="button button-quiet" onClick={onCancel}>Batal</button>
    </div>
  );
}

function BackupPage() {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  async function downloadBackup() {
    setBusy(true);
    setMessage('');
    setError('');
    const tables = ['clinics', 'users', 'staff', 'nakes_schedules', 'clients', 'visits', 'transactions', 'transaction_items', 'payments', 'services', 'bhp_items', 'bhp_stock', 'appointments'];
    const backup = { clinic_id: CLINIC_ID, exported_at: new Date().toISOString(), tables: {}, warnings: [] };
    try {
      for (const table of tables) {
        const { data, error: queryError } = await supabase.from(table).select('*').eq('clinic_id', CLINIC_ID).limit(10000);
        if (queryError) backup.warnings.push(`${table}: ${queryError.message}`);
        else backup.tables[table] = data || [];
      }
      const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `xv-clinic-backup-${new Date().toISOString().slice(0, 10)}.json`;
      link.click();
      URL.revokeObjectURL(url);
      setMessage(`Backup selesai. ${Object.keys(backup.tables).length} tabel diekspor.`);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page-stack">
      <article className="panel" style={{ padding: 24 }}>
        <div className="panel-head">
          <div><span className="eyebrow">Perlindungan Data</span><h3>Backup Data Klinik</h3></div>
        </div>
        <p style={{ color: '#6b7280', lineHeight: 1.6, maxWidth: 760 }}>
          Unduh salinan data klinik dalam format JSON untuk disimpan oleh administrator. Data tetap difilter berdasarkan klinik aktif.
        </p>
        <div className="alert" style={{ background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', margin: '16px 0' }}>
          Untuk tahap awal backup disimpan di komputer administrator. Jangan membagikan file ini karena berisi data pasien.
        </div>
        {error && <div className="alert alert-danger"><strong>Terjadi kesalahan.</strong> {error}</div>}
        {message && <div className="alert alert-success">{message}</div>}
        <button type="button" className="button button-primary" onClick={downloadBackup} disabled={busy}>
          <HardDrive size={16} /> {busy ? 'Menyiapkan backup…' : 'Unduh Backup JSON'}
        </button>
      </article>
      <article className="panel" style={{ padding: 24 }}>
        <span className="eyebrow">Rencana Penyimpanan</span>
        <h3 style={{ marginTop: 4 }}>Backup otomatis ke cloud</h3>
        <p style={{ color: '#6b7280', lineHeight: 1.6, marginBottom: 0 }}>
          Tahap berikutnya dapat memakai Supabase Storage bucket privat dengan enkripsi, retensi harian 30 hari, dan akses khusus Administrator. Backup manual ini menjadi jalur pemulihan sementara sampai Storage dan jadwal otomatis diaktifkan.
        </p>
      </article>
    </div>
  );
}

function NakesPayrollSettings() {
  const [rows, setRows] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [group, setGroup] = useState('NAKES');

  async function fetchRows() {
    setLoading(true);
    setError('');
    try {
      const [staffResult, usersResult, membershipResult, thpResult] = await Promise.all([
        supabase.from('staff')
          .select('id,user_id,name,division,competencies,status,base_salary,allowance_per_attendance,default_commission,payroll_type,profit_share_rate')
          .eq('clinic_id', CLINIC_ID).order('name'),
        supabase.from('users').select('id,name,username,legacy_role,status').eq('status', 'ACTIVE').order('name'),
        supabase.from('clinic_memberships').select('user_id,status').eq('clinic_id', CLINIC_ID).eq('status', 'ACTIVE'),
        supabase.from('clinic_user_thp_settings').select('*').eq('clinic_id', CLINIC_ID).eq('status', 'ACTIVE'),
      ]);
      if (staffResult.error) throw staffResult.error;
      if (usersResult.error) throw usersResult.error;
      if (membershipResult.error) throw membershipResult.error;
      if (thpResult.error) throw thpResult.error;
      const activeIds = new Set((membershipResult.data || []).map((membership) => membership.user_id));
      const staffMap = new Map((staffResult.data || []).map((staff) => [staff.user_id, staff]));
      const settingMap = new Map((thpResult.data || []).map((setting) => [setting.user_id, setting]));
      const nextRows = (usersResult.data || []).filter((user) => activeIds.has(user.id)).map((user) => {
        const staff = staffMap.get(user.id) || {};
        const saved = settingMap.get(user.id) || {};
        const source = `${user.legacy_role || ''} ${staff.competencies || ''} ${staff.division || ''}`.toLowerCase();
        const clinical = !/(marketing|frontline|frontliner|kasir|reseps|gudang|admin|finance|keuangan|customer service|cs\b)/.test(source)
          && /(nakes|dokter|terapis|perawat|bidan|akupunkt|dental|gigi|fisioterapi|apoteker|farmasi|wellness|estetik)/.test(source);
        return {
          ...user,
          ...staff,
          ...saved,
          id: saved.id || user.id,
          user_id: user.id,
          staff_id: staff.id || null,
          user_role: user.legacy_role || '',
          thp_group: saved.thp_group || (clinical ? 'NAKES' : 'NON_NAKES'),
        };
      });
      setRows(nextRows);
      setDrafts(Object.fromEntries(nextRows.map((row) => [row.id, {
        thp_group: row.thp_group || 'NON_NAKES',
        thp_type: row.thp_type || row.payroll_type || (row.user_role || 'LAINNYA'),
        monthly_salary: String(row.monthly_salary ?? row.base_salary ?? 0),
        attendance_allowance: String(row.attendance_allowance ?? row.allowance_per_attendance ?? 0),
        commission_rate: String(row.commission_rate != null ? Number(row.commission_rate) * 100 : Number(row.profit_share_rate ?? 0)),
        notes: row.notes || '',
      }])));
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchRows();
  }, []);

  function updateDraft(id, key, value) {
    setStatus('');
    setError('');
    setDrafts((current) => ({
      ...current,
      [id]: { ...(current[id] || {}), [key]: value },
    }));
  }

  async function saveRow(row) {
    const draft = drafts[row.id] || {};
    setSavingId(row.id);
    setStatus('');
    setError('');
    try {
      const { error: updateError } = await supabase.from('clinic_user_thp_settings').upsert({
        id: row.id?.startsWith('THP-') ? row.id : undefined,
        clinic_id: CLINIC_ID,
        user_id: row.user_id || row.id,
        thp_group: draft.thp_group || 'NON_NAKES',
        thp_type: draft.thp_type || 'LAINNYA',
        monthly_salary: Number(draft.monthly_salary) || 0,
        attendance_allowance: Number(draft.attendance_allowance) || 0,
        commission_rate: (Number(draft.commission_rate) || 0) / 100,
        notes: draft.notes || null,
        status: 'ACTIVE',
        effective_from: new Date().toISOString().slice(0, 10),
        updated_at: new Date().toISOString(),
      }, { onConflict: 'clinic_id,user_id' });
      if (updateError) throw updateError;
      setStatus(`THP ${row.name || row.username} tersimpan dari akun pengguna.`);
      await fetchRows();
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setSavingId('');
    }
  }

  const visibleRows = rows.filter((row) => group === row.thp_group);

  return (
    <article className="panel" style={{ padding: 24 }}>
      <div className="panel-head">
        <div>
          <span className="eyebrow">Kompensasi Tim</span>
          <h3>Pengaturan THP Klinik</h3>
          <p style={{ color: '#6b7280', margin: '4px 0 0' }}>
            Atur THP tenaga klinis dan non-Nakes secara terpisah. Komisi marketing referral 0,5% dihitung otomatis di Payroll non-Nakes.
          </p>
        </div>
      </div>
      {error ? <div className="alert alert-danger"><strong>Terjadi kesalahan.</strong> {error}</div> : null}
      {status ? <div className="alert alert-success"><CheckCircle2 size={16} /> {status}</div> : null}
      {loading ? (
        <div className="content-loading"><div className="loader" /><span>Memuat THP klinik...</span></div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <div className="membership-tabs" role="tablist" aria-label="Kelompok THP" style={{ marginBottom: 14 }}>
            <button type="button" className={`button ${group === 'NAKES' ? 'button-primary' : 'button-quiet'}`} onClick={() => setGroup('NAKES')}>THP Nakes</button>
            <button type="button" className={`button ${group === 'NON_NAKES' ? 'button-primary' : 'button-quiet'}`} onClick={() => setGroup('NON_NAKES')}>THP non-Nakes</button>
          </div>
          <table className="settings-payroll-table">
            <thead>
              <tr>
                <th>{group === 'NAKES' ? 'Nakes' : 'Staf / Marketing'}</th>
                <th>Tipe THP</th>
                <th>Gaji Bulanan</th>
                <th>Tunjangan Kehadiran</th>
                <th>% Bagi Hasil</th>
                <th>Keterangan</th>
                <th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row) => {
                const draft = drafts[row.id] || {};
                return (
                  <tr key={row.id}>
                    <td>
                      <strong>{row.name || row.username}</strong>
                      <div style={{ color: '#6b7280', fontSize: 12, marginTop: 2 }}>
                        {row.username || row.user_role || 'Akun pengguna'} · {row.user_role || 'Tanpa role'}
                      </div>
                    </td>
                    <td>
                      <select value={draft.thp_type || 'LAINNYA'} onChange={(event) => updateDraft(row.id, 'thp_type', event.target.value)}>
                        {['DOKTER','TERAPIS','PERAWAT','MARKETING','FRONTLINE','KASIR','RESEPSIONIS','GUDANG','LAINNYA'].map((type) => <option key={type} value={type}>{type}</option>)}
                      </select>
                    </td>
                    <td><input type="number" min="0" value={draft.monthly_salary || '0'} onChange={(event) => updateDraft(row.id, 'monthly_salary', event.target.value)} /></td>
                    <td><input type="number" min="0" value={draft.attendance_allowance || '0'} onChange={(event) => updateDraft(row.id, 'attendance_allowance', event.target.value)} /></td>
                    <td><input type="number" min="0" max="100" step="0.1" value={draft.commission_rate || '0'} onChange={(event) => updateDraft(row.id, 'commission_rate', event.target.value)} /></td>
                    <td><input type="text" value={draft.notes || ''} placeholder="Opsional" onChange={(event) => updateDraft(row.id, 'notes', event.target.value)} /></td>
                    <td>
                      <button type="button" className="button button-primary" onClick={() => saveRow(row)} disabled={savingId === row.id}>
                        {savingId === row.id ? 'Menyimpan...' : 'Simpan'}
                      </button>
                    </td>
                  </tr>
                );
              })}
              {!visibleRows.length ? (
                <tr><td colSpan={7} style={{ color: '#6b7280', padding: 16 }}>Belum ada akun pengguna pada kelompok ini.</td></tr>
              ) : null}
            </tbody>
          </table>
          <div style={{ color: '#6b7280', fontSize: 12, marginTop: 10 }}>
            Ringkasan {group === 'NAKES' ? 'Nakes' : 'non-Nakes'}: {visibleRows.filter((row) => Number(row.monthly_salary ?? row.base_salary ?? 0) > 0).length} staf memiliki gaji bulanan. Total gaji dasar: {rupiah.format(visibleRows.reduce((sum, row) => sum + Number(row.monthly_salary ?? row.base_salary ?? 0), 0))}.
          </div>
        </div>
      )}
      <style>{`
        .settings-payroll-table { border-collapse: collapse; min-width: 980px; width: 100%; }
        .settings-payroll-table th {
          background: #f3efe6;
          color: #6b6555;
          font-size: 11px;
          letter-spacing: .05em;
          padding: 10px 12px;
          text-align: left;
          text-transform: uppercase;
        }
        .settings-payroll-table td { border-bottom: 1px solid #eee7da; padding: 10px 12px; vertical-align: top; }
        .settings-payroll-table input,
        .settings-payroll-table select {
          background: #fff;
          border: 1px solid #e8e2d5;
          border-radius: 8px;
          box-sizing: border-box;
          font: inherit;
          min-width: 120px;
          padding: 8px 10px;
          width: 100%;
        }
      `}</style>
    </article>
  );
}

export function SettingsPage({ identity, onClinicUpdated }) {
  const [form, setForm]         = useState(null);
  const [app, setApp]           = useState({
    preset: 'tcm-classic', sidebar_color: '', font: 'DM+Sans',
    wallpaper: 'daun', density: 'normal', icon: 'outline',
    animation: 'normal', mode: 'light',
    logo_login: '', logo_invoice: '', wallpaper_login: '',
    quote_login: '', login_style: 'split',
    accent_invoice: '', header_invoice: '',
  });
  const [tab, setTab]           = useState('branding');
  const [busy, setBusy]         = useState(false);
  const [status, setStatus]     = useState(null);
  const [errMsg, setErrMsg]     = useState('');
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [prizes, setPrizes]     = useState([]);
  const [prizeBusy, setPrizeBusy] = useState(false);
  const [prizeStatus, setPrizeStatus] = useState(null);
  const [editingProfileSection, setEditingProfileSection] = useState(null);
  const [openNavGroup, setOpenNavGroup] = useState(null);

  useEffect(() => {
    loadPrizeCatalog().then(setPrizes).catch(() => {});
    loadClinicSettings().then((data) => {
      const c = data.contact || {};
      // Support legacy key names (address, phone, telephone, alamat)
      const cv = (keys) => { for (const k of keys) { if (c[k]) return c[k]; } return ''; };
      setForm({
        name:             data.name           || '',
        legal_name:       data.legal_name     || '',
        timezone:         data.timezone       || 'Asia/Jakarta',
        slogan:           cv(['slogan']),
        clinic_address:   cv(['clinic_address', 'address', 'alamat']),
        clinic_phone:     cv(['clinic_phone', 'phone', 'telephone']),
        email:            cv(['email']),
        website:          cv(['website']),
        logo_url:         cv(['logo_url']),
        default_doctor:   cv(['default_doctor']),
        invoice_footer:   cv(['invoice_footer']),
        invoice_notes:    cv(['invoice_notes']),
        instagram_url:    cv(['instagram_url']),
        google_maps_url:  cv(['google_maps_url']),
        tiktok_url:       cv(['tiktok_url']),
        satusehat_organization_id:      data.satusehat_organization_id || '',
        satusehat_organization_name:    data.satusehat_organization_name || '',
        satusehat_organization_status:  data.satusehat_organization_status || '',
        reservation_slot_wellness:      c.reservation_slot_minutes?.WELLNESS || 60,
        reservation_slot_estetik:       c.reservation_slot_minutes?.ESTETIK || 60,
        reservation_slot_dental:        c.reservation_slot_minutes?.DENTAL || 30,
        reservation_slot_dokter_umum:   c.reservation_slot_minutes?.DOKTER_UMUM || 30,
        reservation_slot_fisioterapi:   c.reservation_slot_minutes?.FISIOTERAPI || 60,
        survey_expiry_minutes:           Number(c.survey_expiry_minutes) || 60,
        operating_hours:                normalizeOperatingHours(c.operating_hours),
      });
      if (c.appearance) setApp((prev) => ({ ...prev, ...c.appearance }));
    }).catch((e) => { setStatus('err'); setErrMsg(e.message || String(e)); });
  }, []);

  function set(key, val)   { setStatus(null); setForm((p) => ({ ...p, [key]: val })); }
  function setA(key, val)  { setStatus(null); setApp((p)  => ({ ...p, [key]: val })); }
  function setOperatingHour(day, field, value) {
    set('operating_hours', {
      ...(form?.operating_hours || defaultOperatingHours()),
      [day]: { ...(form?.operating_hours?.[day] || {}), [field]: value },
    });
  }

  function selectPreset(key) {
    setA('preset', key);
    setActiveTemplate(null);
    applyAppearance({ ...app, preset: key });
  }

  function applyTemplate(t) {
    const next = { ...app, preset: t.preset, wallpaper: t.wallpaper, font: t.font };
    setApp(next);
    setActiveTemplate(t.key);
    applyAppearance(next);
  }

  function previewAppearance(key, val) {
    const next = { ...app, [key]: val };
    setA(key, val);
    applyAppearance(next);
  }

  async function handleSave(e) {
    e.preventDefault();
    setBusy(true); setStatus(null);
    try {
      const contactBase = {
        slogan: form?.slogan, clinic_address: form?.clinic_address,
        clinic_phone: form?.clinic_phone, email: form?.email,
        website: form?.website, logo_url: form?.logo_url,
        default_doctor: form?.default_doctor,
        invoice_footer: form?.invoice_footer, invoice_notes: form?.invoice_notes,
        instagram_url: form?.instagram_url,
        google_maps_url: form?.google_maps_url,
        tiktok_url: form?.tiktok_url,
        reservation_slot_minutes: {
          WELLNESS: Number(form?.reservation_slot_wellness) || 60,
          ESTETIK: Number(form?.reservation_slot_estetik) || 60,
          DENTAL: Number(form?.reservation_slot_dental) || 30,
          DOKTER_UMUM: Number(form?.reservation_slot_dokter_umum) || 30,
          FISIOTERAPI: Number(form?.reservation_slot_fisioterapi) || 60,
        },
        survey_expiry_minutes: Math.min(Math.max(Number(form?.survey_expiry_minutes) || 60, 5), 1440),
        operating_hours: form?.operating_hours || defaultOperatingHours(),
        appearance: app,
      };
      await saveClinicSettings({
        name:       form?.name       || '',
        legal_name: form?.legal_name || '',
        timezone:   form?.timezone   || 'Asia/Jakarta',
        contact:    contactBase,
      });
      await saveSatusehatOrganizationMapping({
        organization_id: form?.satusehat_organization_id || '',
        organization_name: form?.satusehat_organization_name || '',
      });
      applyAppearance(app);
      setStatus('ok');
      setEditingProfileSection(null);
      if (onClinicUpdated) onClinicUpdated({ name: form?.name, legal_name: form?.legal_name });
    } catch (e) {
      setStatus('err'); setErrMsg(e.message || String(e));
    } finally {
      setBusy(false);
    }
  }

  function cancelPreview() {
    loadClinicSettings().then((data) => {
      const saved = data.contact?.appearance || {};
      setApp((prev) => ({ ...prev, ...saved }));
      applyAppearance(saved);
    });
  }

  if (!form) return (
    <div className="page-body">
      <div className="content-loading"><div className="loader" /><p>Memuat pengaturan…</p></div>
    </div>
  );

  const allItems = NAV_GROUPS.flatMap(g => g.items);
  const activeLabel = allItems.find(i => i.key === tab)?.label || 'Pengaturan';

  return (
    <div style={{ minHeight: '100%', background: '#f8fafc' }}>

      {/* ── Content Area ── */}
      <div style={{ width: '100%', boxSizing: 'border-box', padding: '0px clamp(20px, 4vw, 56px) 48px' }}>

        {/* Header / horizontal settings navigation */}
        <style>{`
          .settings-topbar { position: relative; z-index: 5; display: flex; align-items: center; gap: 14px; min-height: 72px; margin-bottom: 24px; padding: 0 18px; color: #f8fafc; background: #102f27; border-radius: 12px; box-shadow: 0 10px 24px rgba(16,47,39,.12); }
          .settings-topbar-title { flex: 0 0 auto; min-width: 150px; margin-right: 4px; }
          .settings-topbar-title p { margin: 0 0 3px; color: #9fe4bf; font-size: 10px; font-weight: 800; letter-spacing: .12em; text-transform: uppercase; }
          .settings-topbar-title h2 { margin: 0; color: #fff; font-size: 20px; font-weight: 800; }
          .settings-topnav { display: flex; align-items: center; gap: 1px; flex: 1; }
          .settings-topnav-group { position: relative; }
          .settings-topnav-button { display: inline-flex; align-items: center; gap: 5px; min-width: 88px; justify-content: center; border: 0; border-radius: 999px; padding: 9px 10px; color: rgba(255,255,255,.82); background: transparent; font: inherit; font-size: 13px; font-weight: 700; cursor: pointer; }
          .settings-topnav-button:hover, .settings-topnav-button.is-open { color: #102f27; background: #f0c979; }
          .settings-topnav-menu { position: absolute; top: 100%; left: -4px; display: grid; grid-template-columns: minmax(190px, 1fr); min-width: 230px; padding: 10px 8px 8px; border: 1px solid #dce8e1; border-radius: 0 0 10px 10px; background: #fffdf8; box-shadow: 0 18px 36px rgba(16,47,39,.18); }
          .settings-topnav-item { display: flex; align-items: center; gap: 9px; width: 100%; border: 0; border-radius: 7px; padding: 10px 11px; color: #243b35; background: transparent; font: inherit; font-size: 13px; font-weight: 650; text-align: left; cursor: pointer; }
          .settings-topnav-item:hover, .settings-topnav-item.is-active { color: #102f27; background: #e7f5ec; }
          .settings-mobile-select { display: none; }
          @media (max-width: 900px) { .settings-topbar { align-items: stretch; flex-direction: column; gap: 12px; padding: 16px; } .settings-topnav { flex-wrap: wrap; } }
          @media (max-width: 640px) { .settings-topnav { display: none; } .settings-mobile-select { display: block; width: 100%; } }
        `}</style>
        <div className="settings-topbar">
          <div className="settings-topbar-title">
            <p>Pengaturan Klinik</p>
            <h2>{activeLabel}</h2>
          </div>
          <nav className="settings-topnav" aria-label="Menu pengaturan">
            {NAV_GROUPS.map((group) => (
              <div className="settings-topnav-group" key={group.label} onMouseEnter={() => setOpenNavGroup(group.label)} onMouseLeave={() => setOpenNavGroup(null)}>
                <button
                  type="button"
                  className={`settings-topnav-button${openNavGroup === group.label ? ' is-open' : ''}`}
                  onClick={() => setOpenNavGroup(group.label)}
                  aria-expanded={openNavGroup === group.label}
                >
                  {group.label} <span aria-hidden="true">⌄</span>
                </button>
                {openNavGroup === group.label ? (
                  <div className="settings-topnav-menu">
                    {group.items.map((item) => {
                      const Icon = item.icon;
                      return <button key={item.key} type="button" className={`settings-topnav-item${tab === item.key ? ' is-active' : ''}`} onClick={() => { setEditingProfileSection(null); setTab(item.key); setOpenNavGroup(null); }}><Icon size={15} />{item.label}</button>;
                    })}
                  </div>
                ) : null}
              </div>
            ))}
          </nav>
          <select className="settings-mobile-select" value={tab} onChange={(e) => { setEditingProfileSection(null); setTab(e.target.value); }} aria-label="Menu pengaturan">
            {NAV_GROUPS.map((group) => <optgroup key={group.label} label={group.label}>{group.items.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}</optgroup>)}
          </select>
        </div>

        {/* ── Non-form tabs: Pengguna, Roles, Backup ── */}
        {tab === 'pengguna' && <PenggunaPage />}
        {tab === 'roles' && <RolesPage />}
        {tab === 'nakes' && <MasterNakesPage />}
        {tab === 'jadwal_nakes' && <JadwalNakesPage />}
        {tab === 'thp_nakes' && <NakesPayrollSettings />}
        {tab === 'master_biaya' && <MasterBiayaPage />}
        {tab === 'patient_portal_access' && <PatientPortalAccessPage />}
        {tab === 'modules' && <ModulesPage identity={identity} onModulesChanged={onClinicUpdated} />}
        {tab === 'backup' && <BackupPage />}

        {/* ── Form-based settings tabs ── */}
        {SETTINGS_TABS.has(tab) && (
        <form onSubmit={handleSave}>
        {status === 'err' && (
          <div className="alert alert-danger" style={{ marginBottom: 16 }}>
            <strong>Terjadi kesalahan.</strong> {errMsg}
          </div>
        )}

        {/* ── Branding Klinik ── */}
        {tab === 'branding' && (
          <div className="operasional-settings" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <style>{`
              .settings-info-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px 24px; }
              .settings-info-field { min-width: 0; padding: 2px 0; }
              .settings-info-field span { display: block; color: #9ca3af; font-size: 11px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; margin-bottom: 4px; }
              .settings-info-field strong { display: block; color: #374151; font-size: 14px; font-weight: 600; overflow-wrap: anywhere; }
              .settings-info-wide { grid-column: 1 / -1; }
              .settings-section-actions { display: flex; align-items: center; gap: 10px; margin-top: 18px; }
              @media (max-width: 700px) { .settings-info-grid { grid-template-columns: 1fr; } .settings-info-wide { grid-column: auto; } }
            `}</style>
            {/* Identitas */}
            <article className="panel">
              <div className="panel-head">
                <div><span className="eyebrow">Identitas Klinik</span><h3>Nama & Kontak</h3></div>
                {editingProfileSection !== 'identity' ? (
                  <button type="button" className="button button-quiet" onClick={() => setEditingProfileSection('identity')}>Edit</button>
                ) : null}
              </div>
              {editingProfileSection === 'identity' ? <>
                <div className="sf-grid">
                  <div className="form-field"><label>Nama Klinik</label>
                    <input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Klinik Xavia TCM" required /></div>
                  <div className="form-field"><label>Slogan Klinik</label>
                    <input value={form.slogan} onChange={(e) => set('slogan', e.target.value)} placeholder="Melayani dengan Sungguh" /></div>
                  <div className="form-field sf-span2"><label>Alamat Klinik</label>
                    <input value={form.clinic_address} onChange={(e) => set('clinic_address', e.target.value)} placeholder="Jl. Contoh No. 1, Jakarta" /></div>
                  <div className="form-field"><label>Telepon Klinik</label>
                    <input value={form.clinic_phone} onChange={(e) => set('clinic_phone', e.target.value)} placeholder="0811-xxxxxxx" /></div>
                  <div className="form-field"><label>Email Klinik</label>
                    <input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="klinik@example.com" /></div>
                  <div className="form-field"><label>Website Klinik</label>
                    <input value={form.website} onChange={(e) => set('website', e.target.value)} placeholder="https://klinik.com" /></div>
                  <div className="form-field"><label>Nama Dokter Default</label>
                    <input value={form.default_doctor} onChange={(e) => set('default_doctor', e.target.value)} placeholder="dr. Nama Dokter" /></div>
                </div>
                <div className="settings-section-actions">
                  <button type="submit" className="button button-primary" disabled={busy}>{busy ? 'Menyimpan…' : 'Simpan'}</button>
                  <button type="button" className="button button-quiet" onClick={() => setEditingProfileSection(null)}>Batal</button>
                </div>
              </> : <div className="settings-info-grid">
                <InfoField label="Nama Klinik" value={form.name} />
                <InfoField label="Slogan Klinik" value={form.slogan} />
                <InfoField label="Alamat Klinik" value={form.clinic_address} wide />
                <InfoField label="Telepon Klinik" value={form.clinic_phone} />
                <InfoField label="Email Klinik" value={form.email} />
                <InfoField label="Website Klinik" value={form.website} />
                <InfoField label="Nama Dokter Default" value={form.default_doctor} />
              </div>}
            </article>

            {/* Media Sosial */}
            <article className="panel">
              <div className="panel-head">
                <div><span className="eyebrow">Media & Branding</span><h3>Sosial Media & Logo</h3></div>
                {editingProfileSection !== 'media' ? (
                  <button type="button" className="button button-quiet" onClick={() => setEditingProfileSection('media')}>Edit</button>
                ) : null}
              </div>
              {editingProfileSection === 'media' ? <>
                <div className="sf-grid">
                  <div className="form-field"><label>Instagram URL</label>
                    <input value={form.instagram_url || ''} onChange={(e) => set('instagram_url', e.target.value)} placeholder="https://instagram.com/klinixv" /></div>
                  <div className="form-field"><label>Google Maps URL</label>
                    <input value={form.google_maps_url || ''} onChange={(e) => set('google_maps_url', e.target.value)} placeholder="https://maps.app.goo.gl/..." /></div>
                  <div className="form-field"><label>TikTok URL</label>
                    <input value={form.tiktok_url || ''} onChange={(e) => set('tiktok_url', e.target.value)} placeholder="https://tiktok.com/@klinixv" /></div>
                  <div className="form-field sf-span2"><label>URL Logo Utama / Google Drive</label>
                    <input value={form.logo_url} onChange={(e) => set('logo_url', e.target.value)} placeholder="https://drive.google.com/..." /></div>
                </div>
                <div className="settings-section-actions">
                  <button type="submit" className="button button-primary" disabled={busy}>{busy ? 'Menyimpan…' : 'Simpan'}</button>
                  <button type="button" className="button button-quiet" onClick={() => setEditingProfileSection(null)}>Batal</button>
                </div>
              </> : <div className="settings-info-grid">
                <InfoField label="Instagram" value={form.instagram_url} />
                <InfoField label="Google Maps" value={form.google_maps_url} />
                <InfoField label="TikTok" value={form.tiktok_url} />
                <InfoField label="Logo Utama" value={form.logo_url} wide />
              </div>}
            </article>

            {/* SATUSEHAT */}
            <article className="panel">
              <div className="panel-head">
                <div><span className="eyebrow">Integrasi</span><h3>SATUSEHAT</h3></div>
                {editingProfileSection !== 'integration' ? (
                  <button type="button" className="button button-quiet" onClick={() => setEditingProfileSection('integration')}>Edit</button>
                ) : null}
              </div>
              {editingProfileSection === 'integration' ? <>
                <div className="sf-grid">
                  <div className="form-field"><label>Organization ID</label>
                    <input value={form.satusehat_organization_id || ''} onChange={(e) => set('satusehat_organization_id', e.target.value)} placeholder="UUID Organization SATUSEHAT" /></div>
                  <div className="form-field"><label>Nama Organization</label>
                    <input value={form.satusehat_organization_name || ''} onChange={(e) => set('satusehat_organization_name', e.target.value)} placeholder="Nama faskes sesuai SATUSEHAT" /></div>
                  <div className="form-field"><label>Status Mapping</label>
                    <input value={form.satusehat_organization_status || (form.satusehat_organization_id ? 'MAPPED' : 'Belum diisi')} readOnly style={{ background: '#f8fafc', color: '#6b7280' }} /></div>
                </div>
                <div className="settings-section-actions">
                  <button type="submit" className="button button-primary" disabled={busy}>{busy ? 'Menyimpan…' : 'Simpan'}</button>
                  <button type="button" className="button button-quiet" onClick={() => setEditingProfileSection(null)}>Batal</button>
                </div>
              </> : <div className="settings-info-grid">
                <InfoField label="Organization ID" value={form.satusehat_organization_id} />
                <InfoField label="Nama Organization" value={form.satusehat_organization_name} />
                <InfoField label="Status Mapping" value={form.satusehat_organization_status || (form.satusehat_organization_id ? 'MAPPED' : 'Belum diisi')} />
              </div>}
            </article>
          </div>
        )}

        {/* ── Operasional ── */}
        {tab === 'operasional' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <style>{`
            .operasional-settings .settings-info-grid { display: grid !important; grid-template-columns: 1fr !important; gap: 8px !important; }
            .operasional-settings .settings-info-field { display: grid !important; grid-template-columns: minmax(180px, 260px) minmax(0, 1fr) !important; align-items: baseline; gap: 20px !important; min-width: 0; padding: 6px 0; border-bottom: 1px solid #eef1ed; }
            .operasional-settings .settings-info-field:last-child { border-bottom: 0; }
            .operasional-settings .settings-info-field span { display: block !important; color: #6b7280; font-size: 14px; font-weight: 500; }
            .operasional-settings .settings-info-field strong { color: #243b35; font-size: 15px; font-weight: 700; overflow-wrap: anywhere; }
            .operating-hours-table { width: 100%; border-collapse: collapse; color: #243b35; }
            .operating-hours-table th { padding: 10px 12px; color: #6b7280; background: #f8f4ec; font-size: 12px; font-weight: 800; letter-spacing: .05em; text-align: left; text-transform: uppercase; }
            .operating-hours-table td { padding: 10px 12px; border-bottom: 1px solid #eef1ed; font-size: 14px; }
            .operating-hours-table tr:last-child td { border-bottom: 0; }
            .operating-hours-table td:first-child { width: 220px; font-weight: 700; }
            .operating-hours-table input[type="time"] { max-width: 150px; }
            .operational-setting-table { width: 100%; border-collapse: collapse; color: #243b35; }
            .operational-setting-table th { padding: 10px 12px; color: #6b7280; background: #f8f4ec; font-size: 12px; font-weight: 800; letter-spacing: .05em; text-align: left; text-transform: uppercase; }
            .operational-setting-table td { padding: 10px 12px; border-bottom: 1px solid #eef1ed; font-size: 14px; }
            .operational-setting-table tr:last-child td { border-bottom: 0; }
            .operational-setting-table td:first-child { width: 220px; font-weight: 700; }
            .operational-setting-table input, .operational-setting-table select, .operational-setting-table textarea { min-height: 42px; box-sizing: border-box; padding: 10px 12px; border: 1px solid #b9e8cf; border-radius: 10px; background: #fff; color: #243b35; font: inherit; }
            .operational-setting-table select { width: 100%; max-width: 260px; }
            .operational-settings .form-field > label { display: block; margin-bottom: 8px; color: #244b3d; font-size: 14px; font-weight: 800; }
            @media (max-width: 640px) {
              .operasional-settings .settings-info-field { grid-template-columns: 1fr; gap: 3px; }
            }
          `}</style>
          <article className="panel">
            <div className="panel-head">
              <div><span className="eyebrow">Waktu & Zona</span><h3>Operasional Klinik</h3></div>
              {editingProfileSection !== 'timezone' ? <button type="button" className="button button-quiet" onClick={() => setEditingProfileSection('timezone')}>Edit</button> : null}
            </div>
            {editingProfileSection === 'timezone' ? <>
              <div className="sf-grid"><div className="form-field"><label>Timezone Operasional</label>
                <select value={form.timezone} onChange={(e) => set('timezone', e.target.value)}>
                  <option value="Asia/Jakarta">Asia/Jakarta — WIB (UTC+7)</option>
                  <option value="Asia/Makassar">Asia/Makassar — WITA (UTC+8)</option>
                  <option value="Asia/Jayapura">Asia/Jayapura — WIT (UTC+9)</option>
                </select>
              </div></div>
              <SectionActions busy={busy} onCancel={() => setEditingProfileSection(null)} />
            </> : <table className="operational-setting-table"><thead><tr><th>Pengaturan</th><th>Nilai</th></tr></thead><tbody><tr><td>Timezone Operasional</td><td>{form.timezone || 'Belum diisi'}</td></tr></tbody></table>}
          </article>
          <article className="panel">
            <div className="panel-head">
              <div><span className="eyebrow">Jam Operasional</span><h3>Jam Buka Klinik</h3></div>
              {editingProfileSection !== 'operating_hours' ? <button type="button" className="button button-quiet" onClick={() => setEditingProfileSection('operating_hours')}>Edit</button> : null}
            </div>
            {editingProfileSection === 'operating_hours' ? <>
              <table className="operating-hours-table">
                <thead><tr><th>Hari</th><th>Jam</th></tr></thead>
                <tbody>{OPERATING_DAYS.map(({ key, label }) => (
                  <tr key={key}>
                    <td>{label}</td>
                    <td><div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <input type="time" value={form.operating_hours?.[key]?.open || '09:00'} onChange={(e) => setOperatingHour(key, 'open', e.target.value)} aria-label={`${label} jam buka`} />
                      <span style={{ color: '#9ca3af', fontSize: 12 }}>sampai</span>
                      <input type="time" value={form.operating_hours?.[key]?.close || '20:00'} onChange={(e) => setOperatingHour(key, 'close', e.target.value)} aria-label={`${label} jam tutup`} />
                    </div></td>
                  </tr>
                ))}</tbody>
              </table>
              <SectionActions busy={busy} onCancel={() => setEditingProfileSection(null)} />
            </> : <table className="operating-hours-table">
              <thead><tr><th>Hari</th><th>Jam</th></tr></thead>
              <tbody>{OPERATING_DAYS.map(({ key, label }) => (
                <tr key={key}><td>{label}</td><td>{`${form.operating_hours?.[key]?.open || '09:00'} – ${form.operating_hours?.[key]?.close || '20:00'}`}</td></tr>
              ))}</tbody>
            </table>}
          </article>
          <article className="panel">
            <div className="panel-head">
              <div><span className="eyebrow">Reservasi Online</span><h3>Durasi Slot per Pasien</h3></div>
              {editingProfileSection !== 'reservation' ? <button type="button" className="button button-quiet" onClick={() => setEditingProfileSection('reservation')}>Edit</button> : null}
            </div>
            {editingProfileSection === 'reservation' ? <>
              <table className="operational-setting-table"><thead><tr><th>Poli</th><th>Durasi Slot</th></tr></thead><tbody>
                {[
                  ['reservation_slot_wellness', 'Wellness / TCM'], ['reservation_slot_estetik', 'Estetik'],
                  ['reservation_slot_dental', 'Dental'], ['reservation_slot_dokter_umum', 'Dokter Umum'],
                  ['reservation_slot_fisioterapi', 'Fisioterapi'],
                ].map(([key, label]) => (
                  <tr key={key}><td>{label}</td><td><select value={form[key]} onChange={(e) => set(key, e.target.value)}>
                    <option value={30}>30 menit</option><option value={45}>45 menit</option><option value={60}>60 menit</option>
                    <option value={90}>90 menit</option><option value={120}>120 menit</option>
                  </select></td></tr>
                ))}
              </tbody></table>
              <SectionActions busy={busy} onCancel={() => setEditingProfileSection(null)} />
            </> : <table className="operational-setting-table"><thead><tr><th>Poli</th><th>Durasi Slot</th></tr></thead><tbody>
              <tr><td>Wellness / TCM</td><td>{form.reservation_slot_wellness} menit</td></tr>
              <tr><td>Estetik</td><td>{form.reservation_slot_estetik} menit</td></tr>
              <tr><td>Dental</td><td>{form.reservation_slot_dental} menit</td></tr>
              <tr><td>Dokter Umum</td><td>{form.reservation_slot_dokter_umum} menit</td></tr>
              <tr><td>Fisioterapi</td><td>{form.reservation_slot_fisioterapi} menit</td></tr>
            </tbody></table>}
          </article>
          </div>
        )}

        {/* ── Invoice & Dokumen ── */}
        {tab === 'invoice' && (
          <article className="panel">
            <style>{`
              .invoice-setting-table { width: 100%; border-collapse: collapse; color: #243b35; }
              .invoice-setting-table th { padding: 10px 12px; color: #6b7280; background: #f8f4ec; font-size: 12px; font-weight: 800; letter-spacing: .05em; text-align: left; text-transform: uppercase; }
              .invoice-setting-table td { padding: 10px 12px; border-bottom: 1px solid #eef1ed; font-size: 14px; vertical-align: top; }
              .invoice-setting-table tr:last-child td { border-bottom: 0; }
              .invoice-setting-table td:first-child { width: 280px; font-weight: 700; }
              .invoice-setting-table td:last-child { overflow-wrap: anywhere; white-space: pre-wrap; }
              .invoice-setting-table input, .invoice-setting-table textarea { width: 100%; box-sizing: border-box; min-height: 42px; padding: 10px 12px; border: 1px solid #b9e8cf; border-radius: 10px; background: #fff; color: #243b35; font: inherit; }
            `}</style>
            <div className="panel-head">
              <div><span className="eyebrow">Dokumen</span><h3>Invoice & Dokumen</h3></div>
              {editingProfileSection !== 'invoice' ? <button type="button" className="button button-quiet" onClick={() => setEditingProfileSection('invoice')}>Edit</button> : null}
            </div>
            {editingProfileSection === 'invoice' ? <>
              <table className="invoice-setting-table"><thead><tr><th>Field</th><th>Nilai</th></tr></thead><tbody>
                <tr><td>Nama Legal (header invoice)</td><td><input value={form.legal_name} onChange={(e) => set('legal_name', e.target.value)} placeholder="Sama dengan nama klinik jika tidak berbeda" /></td></tr>
                <tr><td>Catatan Footer Invoice</td><td><input value={form.invoice_footer} onChange={(e) => set('invoice_footer', e.target.value)} placeholder="Terima kasih telah mempercayai layanan kami." /></td></tr>
                <tr><td>Catatan Tambahan Invoice</td><td><textarea rows={3} value={form.invoice_notes} onChange={(e) => set('invoice_notes', e.target.value)} placeholder="Informasi tambahan yang tampil di invoice…" style={{ resize: 'vertical' }} /></td></tr>
              </tbody></table>
              <SectionActions busy={busy} onCancel={() => setEditingProfileSection(null)} />
            </> : <table className="invoice-setting-table"><thead><tr><th>Field</th><th>Nilai</th></tr></thead><tbody>
              <tr><td>Nama Legal</td><td>{form.legal_name || 'Belum diisi'}</td></tr>
              <tr><td>Catatan Footer Invoice</td><td>{form.invoice_footer || 'Belum diisi'}</td></tr>
              <tr><td>Catatan Tambahan Invoice</td><td>{form.invoice_notes || 'Belum diisi'}</td></tr>
            </tbody></table>}
          </article>
        )}

        {/* ── Branding & Pengalaman ── */}
        {tab === 'pengalaman' && (
          <div className="sp2-panel spe-panel">

            {/* Color Presets */}
            <div className="spe-section-title">
              Theme &amp; Branding Engine
              <span className="spe-section-sub">Klik tema untuk preview langsung — perubahan belum tersimpan sampai klik Simpan.</span>
            </div>
            <div className="spe-preset-grid">
              {COLOR_PRESETS.map((p) => (
                <PresetCard key={p.key} p={p} active={app.preset === p.key} onClick={selectPreset} />
              ))}
              {/* Custom Builder stub */}
              <div className={`spe-preset-card spe-preset-custom`} onClick={() => {}}>
                <div className="spe-preset-dots">
                  {['#f00','#0f0','#00f','#ff0'].map((c,i)=><span key={i} style={{background:c}} className="spe-preset-dot"/>)}
                </div>
                <div className="spe-preset-label">Custom Builder</div>
                <div className="spe-preset-sub">Atur sendiri lengkap</div>
              </div>
            </div>

            <div className="spe-active-row">
              <span>Preset aktif: <strong>{COLOR_PRESETS.find(p=>p.key===app.preset)?.label || '—'}</strong></span>
              <label className="spe-sidebar-pick">
                Sidebar
                <input type="color" value={app.sidebar_color || COLOR_PRESETS.find(p=>p.key===app.preset)?.vars['--green-900'] || '#173f34'}
                  onChange={(e) => previewAppearance('sidebar_color', e.target.value)}
                  className="spe-color-input" />
              </label>
            </div>

            {/* Template Klinik */}
            <div className="spe-section-title" style={{marginTop:'1.2rem'}}>
              Template Klinik
              <span className="spe-section-sub">Satu klik menerapkan paket tema, font, wallpaper, ikon, dan gaya login.</span>
            </div>
            <div className="spe-tpl-grid">
              {TEMPLATE_PRESETS.map((t) => (
                <TemplateCard key={t.key} t={t} active={activeTemplate===t.key} onClick={applyTemplate} />
              ))}
            </div>

            {/* 2-column: Branding Login | Pengalaman Aplikasi */}
            <div className="spe-two-col" style={{marginTop:'1.4rem'}}>
              {/* Branding Login & Invoice */}
              <div className="spe-col-card">
                <div className="spe-col-title">Branding Login &amp; Invoice</div>
                <div className="sp2-field"><label className="sp2-label">Logo Login</label>
                  <input className="input" value={app.logo_login} onChange={(e)=>setA('logo_login',e.target.value)} placeholder="URL gambar logo login" /></div>
                <div className="sp2-field"><label className="sp2-label">Logo Invoice</label>
                  <input className="input" value={app.logo_invoice} onChange={(e)=>setA('logo_invoice',e.target.value)} placeholder="URL gambar logo invoice" /></div>
                <div className="sp2-field"><label className="sp2-label">Wallpaper Login (URL)</label>
                  <input className="input" value={app.wallpaper_login} onChange={(e)=>setA('wallpaper_login',e.target.value)} placeholder="https://..." /></div>
                <div className="sp2-field"><label className="sp2-label">Pesan / Quote Login</label>
                  <input className="input" value={app.quote_login} onChange={(e)=>setA('quote_login',e.target.value)} placeholder="Selamat datang di klinik kami…" /></div>
                <div className="sp2-field"><label className="sp2-label">Gaya Login</label>
                  <select className="input" value={app.login_style} onChange={(e)=>setA('login_style',e.target.value)}>
                    {LOGIN_STYLE_OPT.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
                  </select></div>
                <div className="spe-color-row">
                  <div className="sp2-field"><label className="sp2-label">Accent Invoice</label>
                    <div className="spe-colorpick-wrap">
                      <span className="spe-colorpick-swatch" style={{background: app.accent_invoice||'#2f7d62'}} />
                      <input className="input spe-colorpick-text" value={app.accent_invoice}
                        onChange={(e)=>setA('accent_invoice',e.target.value)} placeholder="#2f7d62" />
                      <input type="color" className="spe-color-input spe-color-input-sm"
                        value={app.accent_invoice||'#2f7d62'} onChange={(e)=>setA('accent_invoice',e.target.value)} />
                    </div>
                  </div>
                  <div className="sp2-field"><label className="sp2-label">Header Tabel Invoice</label>
                    <div className="spe-colorpick-wrap">
                      <span className="spe-colorpick-swatch" style={{background: app.header_invoice||'#e6f3ec'}} />
                      <input className="input spe-colorpick-text" value={app.header_invoice}
                        onChange={(e)=>setA('header_invoice',e.target.value)} placeholder="#e6f3ec" />
                      <input type="color" className="spe-color-input spe-color-input-sm"
                        value={app.header_invoice||'#e6f3ec'} onChange={(e)=>setA('header_invoice',e.target.value)} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Pengalaman Aplikasi */}
              <div className="spe-col-card">
                <div className="spe-col-title">Pengalaman Aplikasi</div>
                {[
                  { label:'Wallpaper Dashboard', key:'wallpaper',  opts: WALLPAPER_OPTIONS.map(o=>({value:o.value,label:o.label})) },
                  { label:'Font',               key:'font',       opts: FONT_OPTIONS.map(o=>({value:o.value,label:o.label})) },
                  { label:'Density',            key:'density',    opts: DENSITY_OPTIONS },
                  { label:'Ikon',               key:'icon',       opts: ICON_OPTIONS },
                  { label:'Animasi',            key:'animation',  opts: ANIM_OPTIONS },
                  { label:'Mode Tampilan',      key:'mode',       opts: MODE_OPTIONS },
                ].map(({ label, key, opts }) => (
                  <div key={key} className="sp2-field">
                    <label className="sp2-label">{label}</label>
                    <select className="input" value={app[key] || opts[0].value}
                      onChange={(e) => previewAppearance(key, e.target.value)}>
                      {opts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>

            {/* Live Preview */}
            <div className="spe-section-title" style={{marginTop:'1.4rem'}}>Live Preview</div>
            <LivePreview app={app} clinicName={form?.name} />

            {/* Save row */}
            <div className="spe-save-row">
              <button className="button button-primary spe-save-main" type="submit" disabled={busy}>
                {busy ? 'Menyimpan…' : 'Simpan Branding & Pengalaman'}
              </button>
              <button className="button button-quiet" type="button" onClick={cancelPreview}>
                Batalkan Preview
              </button>
            </div>
          </div>
        )}

        {/* ── Hadiah Survey ── */}
        {tab === 'hadiah' && (
          <div className="sp2-panel">
            <article className="panel" style={{ marginBottom: 16 }}>
              <div className="panel-head">
                <div><span className="eyebrow">WAKTU SURVEY</span><h3>Batas pengisian survey</h3></div>
              </div>
              <p style={{ color: '#6b7280', fontSize: 13, margin: '0 0 12px' }}>
                Link survey dikirim setelah pembayaran. Atur berapa lama pasien dapat mengisi survey dan memperoleh voucher.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(180px, 280px) 1fr', gap: 12, alignItems: 'end' }}>
                <Field label="Durasi survey (menit)">
                  <input type="number" min="5" max="1440" step="5" value={form.survey_expiry_minutes} onChange={(e) => set('survey_expiry_minutes', e.target.value)} />
                </Field>
                <div style={{ color: '#166534', fontSize: 13, paddingBottom: 10 }}>
                  Default: <strong>60 menit (1 jam)</strong>. Maksimal 24 jam.
                </div>
              </div>
              <div className="settings-section-actions">
                <button type="submit" className="button button-primary" disabled={busy}>
                  {busy ? 'Menyimpan…' : 'Simpan Durasi Survey'}
                </button>
              </div>
            </article>
            <div style={{ marginBottom: 16 }}>
              <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 4px' }}>
                Atur daftar hadiah yang bisa dimenangkan pasien. <strong>Kuota 0 = tidak terbatas.</strong> Weight menentukan peluang — semakin tinggi, semakin sering muncul.
              </p>
            </div>

            {/* Tabel hadiah */}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e5e7eb' }}>
                    {['Emoji','Label Hadiah','Deskripsi','Keterangan Kasir','Kuota','Weight','Diskon (Rp)',''].map((h) => (
                      <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: '#374151', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {prizes.map((p, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '6px 8px' }}>
                        <input value={p.emoji || ''} onChange={(e) => { const n=[...prizes]; n[i]={...n[i],emoji:e.target.value}; setPrizes(n); }} style={{ width: 50, textAlign: 'center', border: '1px solid #e5e7eb', borderRadius: 6, padding: '4px 6px', fontSize: 18 }} />
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <input value={p.label || ''} onChange={(e) => { const n=[...prizes]; n[i]={...n[i],label:e.target.value}; setPrizes(n); }} style={{ width: 160, border: '1px solid #e5e7eb', borderRadius: 6, padding: '4px 8px' }} />
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <input value={p.desc || ''} onChange={(e) => { const n=[...prizes]; n[i]={...n[i],desc:e.target.value}; setPrizes(n); }} style={{ width: 220, border: '1px solid #e5e7eb', borderRadius: 6, padding: '4px 8px' }} />
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <input value={p.notes || ''} onChange={(e) => { const n=[...prizes]; n[i]={...n[i],notes:e.target.value}; setPrizes(n); }} placeholder="Misal: untuk facial reguler" style={{ width: 180, border: '1px solid #e5e7eb', borderRadius: 6, padding: '4px 8px' }} />
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <input type="number" min="0" value={p.quota ?? 0} onChange={(e) => { const n=[...prizes]; n[i]={...n[i],quota:Number(e.target.value)}; setPrizes(n); }} style={{ width: 70, border: '1px solid #e5e7eb', borderRadius: 6, padding: '4px 8px', textAlign: 'center' }} />
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <input type="number" min="1" value={p.weight ?? 10} onChange={(e) => { const n=[...prizes]; n[i]={...n[i],weight:Number(e.target.value)}; setPrizes(n); }} style={{ width: 70, border: '1px solid #e5e7eb', borderRadius: 6, padding: '4px 8px', textAlign: 'center' }} />
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <input type="number" min="0" value={p.discount ?? 0} onChange={(e) => { const n=[...prizes]; n[i]={...n[i],discount:Number(e.target.value)}; setPrizes(n); }} style={{ width: 90, border: '1px solid #e5e7eb', borderRadius: 6, padding: '4px 8px', textAlign: 'right' }} />
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <button type="button" onClick={() => setPrizes(prizes.filter((_,j)=>j!==i))} style={{ background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer' }}>
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Tambah baris */}
            <button type="button" onClick={() => setPrizes([...prizes, { type: `PRIZE_${Date.now()}`, label: '', emoji: '🎁', desc: '', notes: '', discount: 0, quota: 0, weight: 10 }])}
              style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 6, background: '#f0fdf4', color: '#16a34a', border: '1px dashed #86efac', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
              <Plus size={14} /> Tambah Hadiah
            </button>

            {/* Save */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 20 }}>
              <button type="button" disabled={prizeBusy} onClick={async () => {
                setPrizeBusy(true); setPrizeStatus(null);
                try { await savePrizeCatalog(prizes); setPrizeStatus('ok'); }
                catch (e) { setPrizeStatus('err' + (e.message||'')); }
                finally { setPrizeBusy(false); }
              }} className="button button-primary">
                {prizeBusy ? 'Menyimpan…' : 'Simpan Katalog Hadiah'}
              </button>
              {prizeStatus === 'ok' && <span style={{ color: '#16a34a', fontSize: 13, fontWeight: 600 }}>✓ Tersimpan</span>}
              {prizeStatus?.startsWith('err') && <span style={{ color: '#dc2626', fontSize: 13 }}>{prizeStatus.slice(3)}</span>}
            </div>
          </div>
        )}

        {tab === 'pengalaman' && status && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 12 }}>
            {status === 'ok' && <span style={{ color: '#166534', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}><CheckCircle2 size={15} /> Tersimpan.</span>}
            {status === 'err' && <span style={{ color: '#b91c1c', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}><AlertCircle size={15} /> {errMsg}</span>}
          </div>
        )}
        </form>
        )} {/* end SETTINGS_TABS */}

      </div> {/* end content area */}
    </div>
  );
}
